import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  Icon as AuiIcon,
  Action as BaseAction,
  type IconSrc,
  Text,
  colors,
} from '@soluto-private/mx-asurion-ui-react';

import { useAnalytics } from '@service-initiation/shared-data-access-analytics';
import {
  useCurrentPath,
  useGoToStep,
} from '@service-initiation/shared-data-access-flow-manager';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { Route } from '@service-initiation/tech-care-data-access-routing';

import { SectionListItem } from './SectionListItem';

const VisitDetailsSectionListItem = styled.li`
  display: flex;
  justify-content: space-between;
`;

const LeftItemContainer = styled(SectionListItem)``;

const ListItemTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Icon = styled(AuiIcon)`
  align-self: baseline;
`;

const Action = styled(BaseAction)`
  align-self: flex-start;
`;

export type VisitDetailsListItemProps = {
  active?: boolean;
  children?: React.ReactNode;
  iconSrc: IconSrc;
  id?: string;
  linkTo?: keyof typeof Route;
  title: string;
};

export const VisitDetailsListItem: FC<VisitDetailsListItemProps> = ({
  active,
  children,
  iconSrc,
  id,
  linkTo,
  title,
}) => {
  const { t } = useTranslation();
  const currentPath = useCurrentPath();
  const { goToStep } = useGoToStep();
  const { dispatchClickEvent } = useAnalytics();

  return (
    <VisitDetailsSectionListItem id={id}>
      <LeftItemContainer as="div">
        <Icon
          fill={active ? colors.black : colors.neutralDeeper}
          size="large"
          src={iconSrc}
        />
        <ListItemTextContainer>
          <Text
            as="h2"
            color={active ? colors.black : colors.neutralDeeper}
            weight={active ? 'heavy' : 'base'}
          >
            {title}
          </Text>
          {children}
        </ListItemTextContainer>
      </LeftItemContainer>
      {active &&
        linkTo &&
        currentPath !== Route[linkTo] &&
        currentPath !== Route.DamageType &&
        currentPath !== Route.DeviceDetails &&
        currentPath !== Route.Confirmation && (
          <Action
            color="secondary"
            data-testid={`edit-${linkTo}-link`}
            onClick={() => {
              dispatchClickEvent('EditDetail', {
                Cta: t(TranslationEntries.Repairs.Nav.Edit),
                LinkTo: linkTo,
              });
              goToStep(Route[linkTo]);
            }}
            variant="flat"
          >
            {t(TranslationEntries.Repairs.Nav.Edit)}
          </Action>
        )}
    </VisitDetailsSectionListItem>
  );
};
